/**
 * PATH: apps/qa/src/pages/ReportsPage.tsx
 * PURPOSE: Generate and download QA reports and packs — orchestrator
 * WHY: Users need to generate formal reports for inspections, NCR summaries, and QA packs.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐    ┌─────────────┐
 *   │ Generate    │───▶│ Poll status  │───▶│ Download    │───▶│ Share via   │
 *   │ request     │    │ (queued->ok) │    │ PDF/ZIP     │    │ portal link │
 *   └─────────────┘    └──────────────┘    └─────────────┘    └─────────────┘
 */

import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@quotech/auth'
import { reportApi, reportDraftApi } from '@/lib/api'
import type { ReportDraftListItem } from '@/lib/api'
import type { Report } from '@/types/qa'
import { cn } from '@/lib/utils'
import {
  FileText, Clock, RefreshCw, Search, Filter, ChevronDown, X, Plus, Building2, CheckCircle, Pencil,
} from 'lucide-react'

import { QaDraftSheet, QaReportSheet } from './reports/QaReportSheet'
import { GenerateReportDialog } from './reports/GenerateReportDialog'
import { ShareReportDialog } from './reports/ShareReportDialog'
import { EmailReportDialog } from './reports/EmailReportDialog'
import { CreateDraftDialog } from './reports/CreateDraftDialog'
import { typeOptions } from './reports/report-helpers'
import { StandardReports } from './reports/StandardReports'

export default function ReportsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [generateType, setGenerateType] = useState('qa_pack')
  const [showDraftDialog, setShowDraftDialog] = useState(false)
  const [shareReport, setShareReport] = useState<Report | null>(null)
  const [emailReport, setEmailReport] = useState<Report | null>(null)

  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-reports', { type: typeFilter, page, organisationId }],
    queryFn: () => reportApi.getReports({ page, page_size: 20 }),
    enabled: isAuthReady,
  })

  const { data: draftsData } = useQuery({
    queryKey: ['qa-report-drafts', organisationId],
    queryFn: () => reportDraftApi.getDrafts({ page_size: 50 }),
    enabled: isAuthReady,
  })

  const drafts: ReportDraftListItem[] = Array.isArray(draftsData?.results) ? draftsData.results : []
  const showLoading = isLoading || !isAuthReady
  const reports = Array.isArray(data?.results) ? data.results : []
  const areaCoverage = reports.reduce((total, report) => total + report.area_count, 0)
  const checklistItems = reports.reduce((total, report) => total + report.checklist_item_count, 0)
  const filteredReports = reports.filter((report) => {
    if (typeFilter && report.report_type !== typeFilter) return false
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      report.report_name.toLowerCase().includes(query) ||
      report.inspection_name?.toLowerCase().includes(query)
    )
  })

  // Poll for generating reports
  const hasGeneratingReports = data?.results?.some(
    (r) => r.status === 'queued' || r.status === 'generating'
  )
  useEffect(() => {
    if (hasGeneratingReports) {
      const interval = setInterval(() => refetch(), 3000)
      return () => clearInterval(interval)
    }
  }, [hasGeneratingReports, refetch])

  const handleDownload = (report: Report) => window.open(reportApi.getDownloadUrl(report.id), '_blank', 'noopener,noreferrer')

  const handleRegenerate = async (report: Report) => {
    try {
      await reportApi.regenerateReport(report.id)
      refetch()
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to regenerate report:', err)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Reports & QA Packs</h1>
          <p className="text-muted-foreground">Generate and download quality assurance reports</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowDraftDialog(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30">
            <Pencil className="w-4 h-4" />
            Create Editable Report
          </button>
          <button onClick={() => setShowGenerateDialog(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted text-foreground font-medium hover:bg-accent border border-border transition-colors">
            <Plus className="w-4 h-4" />
            Quick Generate
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: FileText, bg: 'bg-primary/10', iconCn: 'text-primary', val: data?.count ?? 0, label: 'Total Reports' },
          { icon: Building2, bg: 'bg-purple-100 dark:bg-purple-900/30', iconCn: 'text-purple-600 dark:text-purple-400', val: areaCoverage, label: 'Areas on page' },
          { icon: CheckCircle, bg: 'bg-emerald-100 dark:bg-emerald-900/30', iconCn: 'text-emerald-600 dark:text-emerald-400', val: checklistItems, label: 'Checklist items' },
          { icon: Clock, bg: 'bg-blue-100 dark:bg-blue-900/30', iconCn: 'text-blue-600 dark:text-blue-400', val: reports.filter((report) => report.status === 'queued' || report.status === 'generating').length, label: 'Generating' },
        ].map(({ icon: Icon, bg, iconCn, val, label }) => (
          <div key={label} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', bg)}>
                <Icon className={cn('w-5 h-5', iconCn)} />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{val}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <StandardReports onGenerate={(reportType) => { setGenerateType(reportType); setShowGenerateDialog(true) }} />

      {/* Editable Drafts Section */}
      <QaDraftSheet drafts={drafts} />

      {showDraftDialog && <CreateDraftDialog isOpen={showDraftDialog} onClose={() => setShowDraftDialog(false)} onSuccess={(draftId) => { setShowDraftDialog(false); navigate(`/reports/draft/${draftId}`) }} />}

      {/* Filters */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input type="text" placeholder="Search reports..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>}
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }} className="appearance-none pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent cursor-pointer min-w-[180px]">
              {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
          <button onClick={() => refetch()} className="p-2.5 rounded-xl border border-input text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {showLoading ? (
        <div className="h-48 animate-pulse rounded-xl border border-border bg-muted/40" />
      ) : error ? (
        <div className="rounded-xl border border-border bg-card py-16 text-center">
          <FileText className="mx-auto mb-4 h-8 w-8 text-destructive" />
          <p className="font-medium text-foreground">Error loading reports</p>
          <p className="mt-1 text-sm text-muted-foreground">Your session may lack report permission, or the service may be unavailable.</p>
          <button onClick={() => refetch()} className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">Try again</button>
        </div>
      ) : filteredReports.length ? (
        <QaReportSheet
          reports={filteredReports}
          onDownload={handleDownload}
          onShare={setShareReport}
          onEmail={setEmailReport}
          onRegenerate={(report) => { void handleRegenerate(report) }}
        />
      ) : (
        <div className="rounded-xl border border-border bg-card py-16 text-center">
          <FileText className="mx-auto mb-4 h-8 w-8 text-muted-foreground" />
          <p className="font-medium text-foreground">No reports found</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{searchQuery || typeFilter ? 'Try adjusting your search or filters' : 'Generate your first report to get started'}</p>
          <button onClick={() => setShowGenerateDialog(true)} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            <Plus className="h-4 w-4" />
            Generate Report
          </button>
        </div>
      )}

      {/* Pagination */}
      {data && data.count > 20 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">Showing <span className="font-medium text-foreground">{((page - 1) * 20) + 1}</span> - <span className="font-medium text-foreground">{Math.min(page * 20, data.count)}</span> of <span className="font-medium text-foreground">{data.count}</span> reports</p>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Previous</button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * 20 >= data.count} className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Next</button>
          </div>
        </div>
      )}

      <GenerateReportDialog isOpen={showGenerateDialog} initialType={generateType} onClose={() => setShowGenerateDialog(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['qa-reports'] })} />
      {shareReport && <ShareReportDialog report={shareReport} isOpen={!!shareReport} onClose={() => setShareReport(null)} />}
      {emailReport && <EmailReportDialog report={emailReport} isOpen={!!emailReport} onClose={() => setEmailReport(null)} />}
    </div>
  )
}
